import React from "react";
import PropTypes from "prop-types";
import { FormattedMessage, useIntl } from "react-intl";
import { Spinner } from "components/Loader";
import styled from "@emotion/styled";
import { colors, fontSize } from "style";

/**
 * AIRoleBuilderProcessing
 *
 * @param {Object}    job           The pending AI role builder job
 * @param {String}    titleId       Intl message ID for the title (optional)
 * @param {String}    descriptionId Intl message ID for the description (optional)
 */
const AIRoleBuilderProcessing = ({
  job,
  titleId = "AIRoleBuilder.Processing.Title",
  descriptionId = "AIRoleBuilder.Processing.Description",
}) => {
  const intl = useIntl();

  return (
    <Container>
      <Title>
        <FormattedMessage id={titleId} />
      </Title>

      <Description>
        <FormattedMessage id={descriptionId} />
      </Description>

      <SpinnerContainer>
        <Spinner size="3rem" />
      </SpinnerContainer>

      <StatusText>{getStatusDisplay(job?.status, intl)}</StatusText>
    </Container>
  );
};

const getStatusDisplay = (status, intl) => {
  if (!status) {
    return <FormattedMessage id="AIRoleBuilder.Processing.Status.Pending" />;
  }

  const translationKey = `AIRoleBuilder.JobStatus.${status}`;

  // Check if translation exists
  if (intl.messages[translationKey]) {
    return <FormattedMessage id={translationKey} />;
  }

  // Fallback: display raw status from data service
  return status;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 2rem;
  text-align: center;
`;

const Title = styled.h3`
  font-size: ${fontSize.large};
  font-weight: 600;
  color: ${colors.grayAnatomyBase};
  margin-bottom: 2rem;
`;

const SpinnerContainer = styled.div`
  margin: 2rem 0;
`;

const StatusText = styled.div`
  font-size: ${fontSize.normal};
  color: ${colors.grayAnatomyLight1};
  margin-top: 2rem;
  margin-bottom: 2rem;
  font-weight: 500;
  max-width: 500px;
`;

const Description = styled.p`
  font-size: ${fontSize.normal};
  color: ${colors.grayAnatomyLight1};
  margin-bottom: 2rem;
  max-width: 500px;
  line-height: 1.5;
`;

AIRoleBuilderProcessing.propTypes = {
  job: PropTypes.shape({
    jobId: PropTypes.string,
    status: PropTypes.string,
    results: PropTypes.object,
  }),
  titleId: PropTypes.string,
  descriptionId: PropTypes.string,
};

export default AIRoleBuilderProcessing;
